export const FONT_PRECONNECT_ORIGINS: ReadonlyArray<{
    href: string;
    crossOrigin?: '';
}> = [
    { href: 'https://fonts.googleapis.com' },
    { href: 'https://fonts.gstatic.com', crossOrigin: '' },
    { href: 'https://cdn.fonts.net' },
];

export const ICON_FONT_STYLESHEET_HREFS: ReadonlyArray<string> = [
    'https://fonts.googleapis.com/icon?family=Material+Icons&display=swap',
    'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,1,0&display=swap',
];

export const BASE_FONT_STYLESHEET_HREFS: ReadonlyArray<string> = [
    'https://cdn.fonts.net/kit/944d7ae0-8f0e-4d00-9762-688d233f67fd/944d7ae0-8f0e-4d00-9762-688d233f67fd_enhanced.css',
];

export const GOOGLE_FONT_STYLESHEETS: Readonly<Record<string, string>> = {
    Anton: 'https://fonts.googleapis.com/css2?family=Anton&display=swap',
    'Nunito Sans':
        'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap',
    'Exo 2': 'https://fonts.googleapis.com/css2?family=Exo+2:ital,wght@0,100..900;1,100..900&display=swap',
    Lato: 'https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap',
    Montserrat:
        'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap',
    'Libre Franklin':
        'https://fonts.googleapis.com/css2?family=Libre+Franklin:ital,wght@0,100..900;1,100..900&display=swap',
    Oswald: 'https://fonts.googleapis.com/css2?family=Oswald:wght@200..700&display=swap',
    Roboto: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap',
    'Roboto Condensed':
        'https://fonts.googleapis.com/css2?family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&display=swap',
    Signika: 'https://fonts.googleapis.com/css2?family=Signika:wght@300..700&display=swap',
    'Francois One': 'https://fonts.googleapis.com/css2?family=Francois+One&display=swap',
    'Mouse Memoirs': 'https://fonts.googleapis.com/css2?family=Mouse+Memoirs&display=swap',
    'Open Sans':
        'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap',
    Boogaloo: 'https://fonts.googleapis.com/css2?family=Boogaloo&display=swap',
    'Source Sans Pro': 'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700&display=swap',
    'Passion One': 'https://fonts.googleapis.com/css2?family=Passion+One:wght@400;700;900&display=swap',
    'Lilita One': 'https://fonts.googleapis.com/css2?family=Lilita+One&display=swap',
    'Noto Sans JP': 'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap',
    'Noto Serif JP': 'https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@200..900&display=swap',
    Krub: 'https://fonts.googleapis.com/css2?family=Krub:ital,wght@0,200;0,300;0,400;0,500;0,600;0,700;1,200;1,300;1,400;1,500;1,600;1,700&display=swap',
    Inter: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
};

export const LOCAL_FONT_FAMILIES: ReadonlyArray<string> = [
    'Aller',
    'BubblegumBliss',
    'Curse Casual',
    'DejaVu Sans',
    'Font Awesome 7 Pro',
    'Gilroy',
    'Glober',
    'HP Simplified',
    'HVD Comic Serif Pro',
    'KingRobotoFlex-Regular',
    'Lemon Milk',
    'LHFAmericanSansRegular',
    'Lithos Pro',
    'Nanum',
    'Neuron',
    'Neuron DemiBold',
    'Neuron Heavy 2',
    'NovemberHebrew',
    'RifficFree',
    'riffic_freebold',
    'Sofia Pro',
    'SofiaSans',
    'Solomon Sans Default',
];

export const ALL_FONT_FAMILIES: ReadonlyArray<string> = [
    ...Object.keys(GOOGLE_FONT_STYLESHEETS),
    ...LOCAL_FONT_FAMILIES,
];

export const SHARED_UI_HARDCODED_FONT_FAMILIES: ReadonlyArray<string> = [
    'Oswald',
    'Lilita One',
    'Inter',
    'Exo 2',
    'Mouse Memoirs',
    'Montserrat',
    'Open Sans',
    'Roboto',
    'Libre Franklin',
    'Krub',
    'Lato',
    'Noto Serif JP',
];

const LOCAL_FONT_FAMILY_KEYS = new Set<string>([
    ...LOCAL_FONT_FAMILIES.map((family) => family.toLowerCase()),
    'solomonsans',
    'kingrobotoflex-regular fallback',
]);

const NORMALIZED_GOOGLE_FONT_STYLESHEETS: Readonly<Record<string, string>> = Object.fromEntries(
    Object.keys(GOOGLE_FONT_STYLESHEETS).map((family) => [
        family.toLowerCase(),
        GOOGLE_FONT_STYLESHEETS[family],
    ])
);

const buildGenericGoogleFontHref = (fontName: string): string => {
    const familyParam = fontName
        .trim()
        .split(/\s+/)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join('+');
    return `https://fonts.googleapis.com/css2?family=${familyParam}&display=swap`;
};

export const resolveGoogleFontHref = (fontName?: string | null): string | undefined => {
    if (!fontName) {
        return undefined;
    }

    const key = fontName.trim().toLowerCase();
    if (key === '' || LOCAL_FONT_FAMILY_KEYS.has(key)) {
        return undefined;
    }

    const curatedHref = NORMALIZED_GOOGLE_FONT_STYLESHEETS[key];
    if (curatedHref !== undefined) {
        return curatedHref;
    }

    return buildGenericGoogleFontHref(fontName);
};

export const resolveThemeFontStylesheets = (
    fontFamilies: ReadonlyArray<string | undefined | null>
): string[] => {
    const hrefs = fontFamilies
        .map((family) => resolveGoogleFontHref(family))
        .filter((href): href is string => href !== undefined);
    return [...new Set(hrefs)];
};
